import React, { useMemo, useRef, useLayoutEffect } from 'react';
import PropTypes from 'prop-types';
import { ResponsiveSankey } from '@nivo/sankey';
import { FaProjectDiagram } from 'react-icons/fa';

export const SANKEY_NODE_COLORS = {
  entry: '#64748b',    // Slate
  step: '#6366f1',     // Indigo (Brand Primary)
  terminal: '#10b981', // Emerald
  exit: '#ef4444'      // Coral / Red
};

export const getNodeType = (node) => {
  if (!node) return 'unknown';
  const rawType = String(node.type || '').toLowerCase();
  if (rawType === 'entry' || rawType === 'terminal' || rawType === 'exit' || rawType === 'step') {
    return rawType;
  }
  const idStr = String(node.id || '').toLowerCase();
  const labelStr = String(node.label || node.displayName || '').toLowerCase();
  if (idStr === 'entry' || idStr === 'launched' || labelStr === 'flow started' || labelStr === 'start') {
    return 'entry';
  }
  if (idStr === 'completed' || labelStr === 'completed' || labelStr === 'completed flow' || labelStr === 'tour completed') {
    return 'terminal';
  }
  if (
    idStr.startsWith('exit') ||
    labelStr.startsWith('drop') ||
    labelStr.startsWith('exit') ||
    labelStr.startsWith('abandon') ||
    /dropped off/i.test(labelStr)
  ) {
    return 'exit';
  }
  if (rawType) return rawType;
  return 'step';
};

export const getStepOrder = (node) => {
  if (!node) return null;
  if (typeof node.stepOrder === 'number') return node.stepOrder;
  if (typeof node.order === 'number') return node.order;
  const labelStr = String(node.label || node.displayName || '');
  const labelMatch = labelStr.match(/^(?:Step\s*|S)(\d+)/i);
  if (labelMatch) return parseInt(labelMatch[1], 10);
  const idStr = String(node.id || '');
  const idMatch = idStr.match(/step_?(\d+)/i);
  if (idMatch) return parseInt(idMatch[1], 10);
  return null;
};

export const getNodeColor = (node) => {
  if (!node) return '#94a3b8';
  const type = getNodeType(node);
  return SANKEY_NODE_COLORS[type] || '#94a3b8';
};

/**
 * Hook to keep Sankey tooltips strictly inside the visible container boundaries.
 * When Nivo anchors a tooltip to the left of a leftmost node (e.g. Start or Step 1),
 * this flips the tooltip to the right of the cursor or clamps it within bounds so it is never
 * cut off, clipped, or underlapped by outer container boundaries.
 */
function useClampedTooltip() {
  const tooltipRef = useRef(null);

  useLayoutEffect(() => {
    const el = tooltipRef.current;
    if (!el || typeof el.getBoundingClientRect !== 'function') return;

    // Reset inline transform to measure Nivo's initial position
    el.style.transform = '';

    const rect = el.getBoundingClientRect();
    if (!rect || rect.width === 0) return;

    const wrapper =
      el.closest('.flow-sankey-chart-wrapper') ||
      el.closest('.flow-sankey-card') ||
      document.body;
    const wrapperRect = wrapper.getBoundingClientRect();
    if (!wrapperRect || wrapperRect.width === 0) return;

    const minLeft = wrapperRect.left + 16;
    const maxRight = wrapperRect.right - 16;

    if (rect.left < minLeft) {
      // If opening off the left edge, flip to the right of the anchor cursor
      const flipRightOffset = rect.width + 28;
      const proposedRight = rect.right + flipRightOffset;
      if (proposedRight <= maxRight) {
        el.style.transform = `translateX(${flipRightOffset}px)`;
      } else {
        const shiftX = minLeft - rect.left;
        el.style.transform = `translateX(${shiftX}px)`;
      }
    } else if (rect.right > maxRight) {
      // If opening off the right edge, shift left back into view
      const shiftX = maxRight - rect.right;
      el.style.transform = `translateX(${shiftX}px)`;
    }
  });

  return tooltipRef;
}

/**
 * Custom tooltip component for Sankey nodes.
 */
const NodeTooltip = ({ node }) => {
  const tooltipRef = useClampedTooltip();
  if (!node) return null;
  const raw = node.data || node;
  const color = raw.nodeColor || getNodeColor(raw);
  const type = getNodeType(raw);
  const typeLabel = type ? type.toUpperCase() : 'STEP';

  // Always show full un-truncated step name in popover
  let title = raw.fullLabel || node.fullLabel || raw.stepName || node.stepName || raw.displayName || raw.label || raw.id;
  if (/^S(\d+)$/i.test(title)) {
    title = title.replace(/^S(\d+)$/i, 'Step $1');
  }
  if (type === 'entry' && (!title || title === 'launched' || title === 'Start')) {
    title = 'Flow Launched';
  } else if ((type === 'terminal' || raw.id === 'completed') && (!title || title === 'completed' || title === 'Completed')) {
    title = 'Journey Completed';
  }

  return (
    <div ref={tooltipRef} className="sankey-tooltip" data-testid="sankey-node-tooltip">
      <div className="sankey-tooltip-header">
        <span className="tooltip-color-dot" style={{ backgroundColor: color }} />
        <strong className="tooltip-title">{title}</strong>
        <span className={`tooltip-type-pill pill-${raw.type || 'step'}`}>{typeLabel}</span>
      </div>
      <div className="sankey-tooltip-body">
        <div className="tooltip-row">
          <span>Users Reached:</span>
          <strong>{(raw.count ?? raw.value ?? raw.viewCount ?? 0).toLocaleString()}</strong>
        </div>
        {raw.completionRate !== undefined && raw.completionRate !== null && (
          <div className="tooltip-row">
            <span>Step Conversion:</span>
            <strong>{raw.completionRate}%</strong>
          </div>
        )}
        {raw.exitCount !== undefined && raw.exitCount !== null && (
          <div className="tooltip-row tooltip-dropoff">
            <span>Step Drop-offs:</span>
            <strong>{raw.exitCount.toLocaleString()}</strong>
          </div>
        )}
      </div>
    </div>
  );
};

NodeTooltip.propTypes = {
  node: PropTypes.object
};

/**
 * Custom tooltip component for Sankey links.
 */
const LinkTooltip = ({ link }) => {
  const tooltipRef = useClampedTooltip();
  if (!link) return null;
  const sourceLabel = link.source?.data?.label || link.source?.label || link.source?.id || 'Start';
  const targetLabel = link.target?.data?.label || link.target?.label || link.target?.id || 'End';
  const val = link.data?.realCount ?? link.realCount ?? link.value ?? 0;

  return (
    <div ref={tooltipRef} className="sankey-tooltip" data-testid="sankey-link-tooltip">
      <div className="sankey-tooltip-header">
        <strong className="tooltip-link-flow">
          {sourceLabel} <span className="tooltip-arrow">➔</span> {targetLabel}
        </strong>
      </div>
      <div className="sankey-tooltip-body">
        <div className="tooltip-row">
          <span>Progression Volume:</span>
          <strong>{val.toLocaleString()} users</strong>
        </div>
      </div>
    </div>
  );
};

LinkTooltip.propTypes = {
  link: PropTypes.object
};

/**
 * Format Sankey node label with smart truncation and streamlined prefixes.
 * Displays step names for step nodes, truncated to fit horizontal spacing without overlap.
 */
export const formatSankeyLabel = (node, totalStepsOrMax = 0, maxLength = undefined) => {
  if (!node) return '';
  const type = getNodeType(node);
  const order = getStepOrder(node);
  const rawLabel = node.label || node.displayName || (order != null ? `Step ${order}` : String(node.id || ''));

  // Support both (node, stepCount, maxLength) and (node, maxLength) signatures
  let stepCount = 0;
  let effectiveMax = 22;

  if (typeof maxLength === 'number') {
    // Explicit 3-argument call: formatSankeyLabel(node, stepCount, maxLength)
    stepCount = totalStepsOrMax;
    effectiveMax = maxLength;
  } else if (typeof totalStepsOrMax === 'number' && totalStepsOrMax > 0) {
    // 2-argument call:
    // If the node has an identified step order, totalStepsOrMax represents the flow's stepCount
    // Otherwise, it represents the maxLength truncation limit for unnumbered / arbitrary nodes
    if (order != null) {
      stepCount = totalStepsOrMax;
      effectiveMax = 22;
    } else {
      stepCount = 0;
      effectiveMax = totalStepsOrMax;
    }
  }

  if (type === 'entry') return 'Start';
  if (type === 'terminal') return 'Completed';
  if (type === 'exit') {
    if (rawLabel === 'Flow Drop-offs' || rawLabel.startsWith('Flow Drop')) {
      return 'Drop-offs';
    }
    let display = rawLabel;
    if (/^Dropped off at Step\s*(\d+)/i.test(display)) {
      const match = display.match(/^Dropped off at Step\s*(\d+)/i);
      display = stepCount > 6 ? `Drop S${match[1]}` : `Drop: Step ${match[1]}`;
    } else if (display.startsWith('Drop-off (')) {
      display = display.replace(/^Drop-off \((.*)\)$/, 'Drop: $1');
    } else if (/^Abandoned before /i.test(display)) {
      display = display.replace(/^Abandoned before /i, 'Exit: ');
    } else if (/^Abandoned /i.test(display)) {
      display = display.replace(/^Abandoned /i, 'Exit: ');
    }
    if (display.length > effectiveMax) {
      return `${display.slice(0, effectiveMax - 1)}…`;
    }
    return display;
  }

  // Step nodes density-aware formatting
  if (type === 'step') {
    // Ultra-dense flows (> 12 steps): compact step tokens (e.g. S1, S2, ..., S22, S23)
    if (stepCount > 12) {
      return order != null ? `S${order}` : (rawLabel.length > 8 ? `${rawLabel.slice(0, 7)}…` : rawLabel);
    }
    // Medium-density flows (7-12 steps): concise step numbers (e.g. Step 14)
    if (stepCount > 6) {
      return order != null ? `Step ${order}` : (rawLabel.length > 12 ? `${rawLabel.slice(0, 11)}…` : rawLabel);
    }
  }

  // Short flows (<= 6 steps) or nodes without stepOrder: full step name with truncation
  if (rawLabel.length > effectiveMax) {
    return `${rawLabel.slice(0, effectiveMax - 1)}…`;
  }
  return rawLabel;
};

/**
 * Visual Sankey diagram component using @nivo/sankey.
 */
export default function FlowSankeyChart({
  data,
  height = 560,
  minHeight = 520,
  enhancedVisibility = true
}) {
  // Normalize nodes and links for @nivo/sankey format
  const chartData = useMemo(() => {
    if (!data || !Array.isArray(data.nodes) || data.nodes.length === 0) {
      return null;
    }

    const rawNodes = data.nodes;
    const rawLinks = Array.isArray(data.links) ? data.links : [];

    // Count step nodes and detect maximum step order
    const stepNodes = rawNodes.filter((n) => getNodeType(n) === 'step');
    const maxOrder = stepNodes.reduce((max, n) => {
      const o = getStepOrder(n);
      return o ? Math.max(max, o) : max;
    }, stepNodes.length);
    const stepCount = Math.max(stepNodes.length, maxOrder);

    // Check if exit nodes should be consolidated to avoid right-column barcode clutter
    const rawExitNodes = rawNodes.filter((n) => getNodeType(n) === 'exit');
    const shouldConsolidateExits = rawExitNodes.length > 5 || stepCount > 8;
    const exitNodeIdSet = new Set(rawExitNodes.map((n) => String(n.id)));

    const entryNode = rawNodes.find((n) => getNodeType(n) === 'entry') || rawNodes[0];
    const totalLaunched = Number(
      entryNode?.count ??
      entryNode?.viewCount ??
      entryNode?.value ??
      100
    );

    let nodes = [];
    if (shouldConsolidateExits) {
      const totalExitCount = rawExitNodes.reduce(
        (sum, n) => sum + (Number(n.count ?? n.exitCount ?? n.value ?? 0)),
        0
      );

      // Keep non-exit nodes
      nodes = rawNodes
        .filter((n) => !exitNodeIdSet.has(String(n.id)))
        .map((n, idx) => {
          const type = getNodeType(n);
          const order = getStepOrder(n) ?? (type === 'step' ? idx + 1 : null);
          const fullLabel = n.fullLabel || n.stepName || n.name || n.label || n.displayName || (order != null ? `Step ${order}` : String(n.id));
          return {
            id: String(n.id),
            label: fullLabel,
            fullLabel,
            type,
            stepOrder: order,
            count: n.count ?? n.viewCount ?? n.value ?? 0,
            completionRate: n.completionRate ?? null,
            exitCount: n.exitCount ?? null,
            nodeColor: getNodeColor({ ...n, type })
          };
        });

      // Add single consolidated exit node
      if (totalExitCount > 0) {
        nodes.push({
          id: 'exit_dropoff',
          label: 'Flow Drop-offs',
          fullLabel: 'Flow Drop-offs',
          type: 'exit',
          count: totalExitCount,
          completionRate: null,
          exitCount: totalExitCount,
          nodeColor: SANKEY_NODE_COLORS.exit
        });
      }
    } else {
      nodes = rawNodes.map((n, idx) => {
        const type = getNodeType(n);
        const order = getStepOrder(n) ?? (type === 'step' ? idx + 1 : null);
        const fullLabel = n.fullLabel || n.stepName || n.name || n.label || n.displayName || (order != null ? `Step ${order}` : String(n.id));
        return {
          id: String(n.id),
          label: fullLabel,
          fullLabel,
          type,
          stepOrder: order,
          count: n.count ?? n.viewCount ?? n.value ?? 0,
          completionRate: n.completionRate ?? null,
          exitCount: n.exitCount ?? null,
          nodeColor: getNodeColor({ ...n, type })
        };
      });
    }

    // Remap and merge links with perceptual scaling for high-dropoff flows
    const nodeIds = new Set(nodes.map((n) => n.id));
    const linkMap = new Map();

    rawLinks.forEach((l) => {
      const source = String(l.source);
      let target = String(l.target);
      const isExit =
        (shouldConsolidateExits && exitNodeIdSet.has(target)) ||
        target === 'exit_dropoff' ||
        exitNodeIdSet.has(target);

      if (shouldConsolidateExits && exitNodeIdSet.has(target)) {
        target = 'exit_dropoff';
      }

      const realVal = Number(l.value) || 0;
      if (realVal > 0 && nodeIds.has(source) && nodeIds.has(target)) {
        let visualVal = realVal;
        if (enhancedVisibility && totalLaunched > 0) {
          if (isExit) {
            // Cap exit link visual value so heavy drop-offs do not overwhelm the diagram (>40% of flow)
            const exitCeiling = Math.max(1, Math.round(totalLaunched * 0.40));
            visualVal = Math.min(realVal, exitCeiling);
          } else {
            // Apply visual floor (~25% of total flow) to forward progress links so downstream steps remain visible
            const visualFloor = Math.max(1, Math.round(totalLaunched * 0.25));
            visualVal = Math.max(realVal, visualFloor);
          }
        }

        const key = `${source}->${target}`;
        const existing = linkMap.get(key);
        if (existing) {
          existing.value += visualVal;
          existing.realCount += realVal;
        } else {
          linkMap.set(key, {
            source,
            target,
            value: visualVal,
            realCount: realVal
          });
        }
      }
    });

    const links = Array.from(linkMap.values());

    if (nodes.length === 0 || links.length === 0) {
      return null;
    }

    return { nodes, links, stepCount };
  }, [data, enhancedVisibility]);

  // Compute dynamic height based on column density when not explicitly forced
  const computedHeight = useMemo(() => {
    if (typeof height === 'number' && height > 0) {
      return height;
    }
    if (!chartData) {
      return minHeight;
    }

    const exitCount = chartData.nodes.filter((n) => getNodeType(n) === 'exit').length;
    const hasTerminal = chartData.nodes.some((n) => n.id === 'completed' || getNodeType(n) === 'terminal');
    const rightColCount = exitCount + (hasTerminal ? 1 : 0);

    const maxColDensity = Math.max(rightColCount, 1);
    const dynamicVal = maxColDensity * 44 + 80;
    return Math.max(minHeight, Math.min(720, dynamicVal));
  }, [height, minHeight, chartData]);

  if (!chartData) {
    return (
      <div className="sankey-empty-state" data-testid="flow-sankey-empty">
        <FaProjectDiagram className="empty-state-icon" />
        <h4>No Journey Telemetry</h4>
        <p>No recorded step transitions exist for this flow within the selected measurement window.</p>
      </div>
    );
  }

  const stepCount = chartData?.stepCount || 0;
  const isDenseFlow = stepCount > 4;
  // Enforce minimum width per step column in dense flows so nodes and ribbons have breathing room
  const minChartWidth = stepCount > 8 ? `${Math.max(1000, stepCount * 75)}px` : '100%';

  return (
    <div
      className="flow-sankey-chart-wrapper"
      style={{
        height: computedHeight,
        overflowX: stepCount > 8 ? 'auto' : 'visible',
        overflowY: 'hidden'
      }}
      data-testid="flow-sankey-chart"
    >
      <div style={{ minWidth: minChartWidth, width: '100%', height: '100%' }}>
        <ResponsiveSankey
          data={chartData}
          margin={{
            top: 24,
            right: isDenseFlow ? 160 : 130,
            bottom: 50,
            left: 120
          }}
          align="start"
          nodeSort={(a, b) => {
            const rank = { entry: 0, step: 1, terminal: 2, exit: 3 };
            const ra = rank[getNodeType(a)] ?? 1;
            const rb = rank[getNodeType(b)] ?? 1;
            if (ra !== rb) return ra - rb;
            const orderA = getStepOrder(a);
            const orderB = getStepOrder(b);
            if (orderA != null && orderB != null) {
              return orderA - orderB;
            }
            return 0;
          }}
          linkSort={(a, b) => {
            const isExitA = getNodeType(a.target) === 'exit' || String(a.target?.id || '').includes('exit');
            const isExitB = getNodeType(b.target) === 'exit' || String(b.target?.id || '').includes('exit');
            if (isExitA !== isExitB) {
              return isExitA ? 1 : -1;
            }
            return 0;
          }}
          colors={(node) => node.nodeColor || getNodeColor(node)}
          nodeOpacity={1}
          nodeHoverOthersOpacity={0.25}
          nodeThickness={20}
          nodeSpacing={20}
          nodeBorderWidth={0}
          nodeBorderRadius={4}
          linkOpacity={0.42}
          linkHoverOthersOpacity={0.12}
          linkContract={1}
          enableLinkGradient={true}
          labelPosition="outside"
          labelOrientation="horizontal"
          labelPadding={12}
          labelTextColor={(node) => {
            const type = getNodeType(node);
            if (type === 'terminal') return '#059669';
            if (type === 'exit') return '#dc2626';
            if (type === 'entry') return '#475569';
            return '#334155';
          }}
          label={(node) => formatSankeyLabel(node, stepCount, 22)}
          nodeTooltip={NodeTooltip}
          linkTooltip={LinkTooltip}
          theme={{
            labels: {
              text: {
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: '-0.2px'
              }
            }
          }}
        />
      </div>
    </div>
  );
}

FlowSankeyChart.propTypes = {
  data: PropTypes.shape({
    nodes: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
        label: PropTypes.string,
        type: PropTypes.string,
        count: PropTypes.number,
        completionRate: PropTypes.number,
        exitCount: PropTypes.number
      })
    ),
    links: PropTypes.arrayOf(
      PropTypes.shape({
        source: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
        target: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
        value: PropTypes.number.isRequired
      })
    )
  }),
  height: PropTypes.number,
  minHeight: PropTypes.number
};
